import type { IContact } from 'src/pages/contacts/contacts.types';

import { toast } from 'sonner';
import { useBoolean } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect } from 'react';

import { Box, Avatar, Popover, Tooltip, useTheme, Typography, IconButton } from '@mui/material';

import { useTranslate } from 'src/locales';
import { ContactForm } from 'src/pages/contacts/components/contact-form';
import { useCreateContactMutation } from 'src/pages/contacts/slice/contactsApi';

import { Iconify } from 'src/components/iconify';

import { DTMFButtons } from './call/call-dtmf';
import { TransferUser } from './call/transfer-user';
import { truncateText } from '../utils/get-message';
import { useCallContext } from '../hooks/use-call-context';

export function CallPopup() {
  const {
    incomingCall,
    rejectCall,
    pauseCall,
    resumeCall,
    muteCall,
    unmuteCall,
    isMuted,
    isPause,
    transferCall,
    sendDTMF,
    CallPopupMethod,
    callUserDetails,
  } = useCallContext();

  const { t } = useTranslate('translation');
  const contactMenu = useBoolean();
  const [createContact, { isLoading: isCreating, error: createError }] = useCreateContactMutation();
  const [selectedUser, setSelectedUser] = useState<IContact | undefined>(undefined);
  const [dialedNumber, setDialedNumber] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElTransfer, setAnchorElTransfer] = useState<HTMLElement | null>(null);

  const keys = [
    { main: '1', sub: '' },
    { main: '2', sub: 'ABC' },
    { main: '3', sub: 'DEF' },
    { main: '4', sub: 'GHI' },
    { main: '5', sub: 'JKL' },
    { main: '6', sub: 'MNO' },
    { main: '7', sub: 'PQRS' },
    { main: '8', sub: 'TUV' },
    { main: '9', sub: 'WXYZ' },
    { main: '*', sub: '' },
    { main: '0', sub: '' },
    { main: '#', sub: '' },
  ];

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio('/sounds/keypress.mp3');
    audioRef.current.load();
  }, []);

  const handleDTMF = (key: string) => {
    setDialedNumber((prev) => prev + key);
    sendDTMF(key);

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((err) => {
        console.error('Audio play error:', err);
      });
    }
  };

  const handleKeypadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleCloseKeypad = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleTransferMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTransfer(anchorElTransfer ? null : event.currentTarget);
  };

  const handleCloseTransfer = () => {
    setAnchorElTransfer(null);
  };

  const openTransfer = Boolean(anchorElTransfer);

  const handleSubmitData = async (account: Partial<IContact>) => {
    function convertNullToString(obj: any) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, value === '' ? null : value])
      );
    }
    try {
      await createContact(convertNullToString(account))
        .unwrap()
        .then((res) => {
          toast.success(t('CONTACT_CREATED_SUCCESSFULLY'));
        })
        .catch((err) => {
          // console.error('Error creating contact:', err);
          toast.error(t('CONTACT_CREATION_FAILED'));
        });

      contactMenu.onFalse();
    } catch (err) {
      console.error('Contact (onSubmit) Error:', err);
    }
  };

  const onCloseHandler = () => {
    contactMenu.onFalse();
  };

  useEffect(() => {
    if (!incomingCall) {
      setAnchorEl(null);
      setAnchorElTransfer(null);
    }
  }, [incomingCall]);

  useEffect(() => {
    if (contactMenu.value && !callUserDetails && incomingCall?.number) {
      setSelectedUser({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        city: '',
        pincode: '',
        tenantId: '',
        crNumber: '',
        website: '',
        country: '',
        ProfileURL: null,
        createdAt: '',
        updatedAt: '',
        companyId: '',
        assignedId: '',
        assignedType: '',
        nationalId: '',
        labels: [],
        phone: `+966${incomingCall.number}`,
      });
    } else if (!contactMenu.value) {
      setSelectedUser(undefined);
    }

    return () => {
      setSelectedUser(undefined);
    };
  }, [contactMenu.value, incomingCall?.number]);

  const displayName =
    callUserDetails?.firstName || callUserDetails?.lastName || callUserDetails?.name
      ? `${callUserDetails?.firstName || ''} ${callUserDetails?.lastName || ''}`.trim() ||
        callUserDetails?.name
      : callUserDetails?.phone || incomingCall?.username || 'Unknown';

  return (
    <>
      <Popover
        open={CallPopupMethod.value && Boolean(incomingCall)}
        // open
        onClose={CallPopupMethod.onFalse}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 16, left: window.innerWidth / 2 }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            pointerEvents: 'auto',
            boxShadow: 'none',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              width: '450px',
              p: 0,
            },
          },
          root: {
            sx: {
              backgroundColor: 'transparent !important',
            },
          },
        }}
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        disableScrollLock
      >
        <Box
          sx={{
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Caller Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '40%',
              gap: 2,
            }}
          >
            <Avatar
              alt={callUserDetails?.firstName || callUserDetails?.lastName || callUserDetails?.name}
              src={
                callUserDetails?.ProfileURL || callUserDetails?.firstName || callUserDetails?.name
              }
              sx={{ width: 48, height: 48, ml: 1 }}
            />
            <Box>
              <Tooltip title={displayName}>
                <Typography variant="h6" sx={{ my: 0.5 }}>
                  {truncateText(displayName, 15)}
                </Typography>
              </Tooltip>

              {/* COMMENT CODE FOR THE ACCOUNT NAME FOR THE CALL */}
              {/* {callUserDetails?.company && (
                <Tooltip title={callUserDetails?.company?.name ?? ''}>
                  <Typography variant="body1" color="text.secondary" sx={{ my: 0.5 }}>
                    {truncateText(callUserDetails?.company?.name, 12)}
                  </Typography>
                </Tooltip>
              )} */}

              <Typography variant="body2" color="text.secondary">
                {callUserDetails?.phone || incomingCall?.number || 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              width: '60%',
            }}
          >
            {!callUserDetails && incomingCall?.number && (
              <Tooltip title={`${t('CREATE')} ${t('CONTACT')}`}>
                <IconButton onClick={contactMenu.onTrue}>
                  <Iconify icon="mdi-user-box" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('KEYPAD')}>
              <IconButton onClick={handleKeypadClick}>
                <Iconify icon="famicons:keypad-sharp" />
              </IconButton>
            </Tooltip>

            {isMuted ? (
              <Tooltip title={t('UNMUTE')}>
                <IconButton onClick={unmuteCall}>
                  <Iconify icon="ep:mute" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('MUTE')}>
                <IconButton onClick={muteCall}>
                  <Iconify icon="solar:microphone-bold" />
                </IconButton>
              </Tooltip>
            )}

            {isPause ? (
              <Tooltip title={t('RESUME')}>
                <IconButton onClick={resumeCall}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path fill="currentColor" d="M6 18V6h2v12zm4 0l10-6l-10-6z" />
                  </svg>
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('HOLD')}>
                <IconButton onClick={pauseCall}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                  >
                    <path fill="currentColor" d="M14 19V5h4v14zm-8 0V5h4v14z" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={t('TRANSFER')}>
              <IconButton onClick={handleTransferMenu}>
                <Iconify icon="mingcute-transfer-line" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('END_CALL')}>
              <IconButton
                onClick={rejectCall}
                color="error"
                sx={{
                  bgcolor: 'error.lighter',
                  '&:hover': {
                    bgcolor: 'error.light',
                  },
                }}
              >
                <Iconify icon="solar:end-call-bold" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Popover>

      {/* Keypad Popover */}
      <DTMFButtons
        open={open && CallPopupMethod.value && Boolean(incomingCall)}
        anchorEl={anchorEl}
        handleClose={handleCloseKeypad}
        keys={keys}
        handleDTMF={handleDTMF}
        theme={theme}
      />
      <TransferUser
        open={openTransfer && CallPopupMethod.value && Boolean(incomingCall)}
        anchorEl={anchorElTransfer}
        handleClose={handleCloseTransfer}
        onTransfer={transferCall}
      />

      <ContactForm
        open={contactMenu.value}
        onClose={onCloseHandler}
        handleSubmitData={handleSubmitData}
        selectedUser={selectedUser}
        isCallPopup
      />
    </>
  );
}
